/**
 * PR-A2: screen-reader result-count announcement.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx. One visually-hidden live
 * region announces the count once per commit (stable list) or once per visible
 * paint (legacy) — never per SSE event. The visible count header keeps its
 * dynamic text but is NOT a live region, so per-event churn stays silent.
 */
import { useEffect, useRef, useState } from "react";

interface IAiSearchAnnouncerProps {
    /** Visible result count: committed rows plus appended rows. */
    resultCount: number;
    stableListEnabled: boolean;
    /** PR-A1: true once the one-time commit has published the buffered list. */
    committed: boolean;
    searchId: string;
}

export function AiSearchAnnouncer(props: IAiSearchAnnouncerProps) {
    const { resultCount, stableListEnabled, committed, searchId } = props;

    const [announcement, setAnnouncement] = useState("");
    // Guards the flag-on once-per-commit contract: appended results change
    // resultCount after the commit but must not re-announce (they render
    // below the fold and must not interrupt the user's screen reader).
    const announcedSearchIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (resultCount === 0) return; // nothing visible; empty state has its own live region
        if (stableListEnabled) {
            if (!committed) return; // skeletons up — nothing painted yet
            if (announcedSearchIdRef.current === searchId) return;
            announcedSearchIdRef.current = searchId;
        }
        setAnnouncement(resultCount === 1 ? "1 result" : `${resultCount} results`);
    }, [resultCount, stableListEnabled, committed, searchId]);

    // Cleared per search so a repeat search announcing the SAME count still
    // fires: aria-live only speaks on text CHANGE. Declared AFTER the announce
    // effect so a searchId change clears last search's text rather than
    // stomping this search's announcement.
    useEffect(() => {
        setAnnouncement("");
    }, [searchId]);

    return (
        <span
            className="sr-only"
            aria-live="polite"
            aria-atomic="true"
            data-testid="ai-search-announcement"
        >
            {announcement}
        </span>
    );
}
